import * as Raven from "raven-js";
import * as React from "react";
import "./index.css";
import { HashRouter, Route } from "react-router-dom";
import { AgencyLoginActivity } from "./activities/AgencyLoginActivity";
import NavBar from "./components/widgets/NavBar";
import { API, APIConfig } from "./API";
import * as ReactDOM from "react-dom";
import { CaregiverLandingScreen } from "./components/CaregiverLandingScreen";
import assertNever from "assert-never";
import { Caregiver } from "./messages/Caregiver";
import { localApiConfig, productionApiConfig, stagingApiConfig } from "./LocalStore";
import { ShortCodeAuth } from "./activities/ShortCodeAuth";
import { AgencyMemberAuthInfo } from "./messages/Auth";
import { Provider } from "react-redux";
import CaregiverFormEntryActivity from "./activities/CaregiverFormEntryActivity";
import CaregiverPhoneEntryActivity from "./activities/CaregiverPhoneEntryActivity";
import ExistingCaregiverBasicDetailsActivity from "./activities/ExistingCaregiverBasicDetailsActivity";
import store from "./store/store";
import NewCaregiverBasicDetailsActivity from "./activities/NewCaregiverBasicDetailActivity";
import { CaregiverCertification } from "./messages/ObjectIds";

export type LoginState = LoginState.NoAuth | LoginState.WithAgencyMemberAuthInfo | LoginState.WithCaregiverAuthInfo | LoginState.NewCaregiver | LoginState.EmptyState;

export const apiConfig: APIConfig = productionApiConfig;
// export const apiConfig: APIConfig = productionApiConfig;
// { apiBaseUrl: "http://10.0.0.56:3000" };
// productionApiConfig;
// stagingApiConfig;
// localApiConfig;

let idleLogoutTime: number = 10 * 60 * 1000;

namespace LoginState {
    export interface NoAuth {
        type: "NoAuth";
    }

    export interface WithAgencyMemberAuthInfo {
        type: "WithAgencyMemberAuthInfo";
        agencyMemberAuthInfo: AgencyMemberAuthInfo;
    }

    export interface NewCaregiver {
        type: "NewCaregiver";
        phoneNumber: string;
        authToken: string;
        apiConfig: APIConfig;
        agencyMemberAuthInfo: AgencyMemberAuthInfo;
    }

    export interface WithCaregiverAuthInfo {
        type: "WithCaregiverAuthInfo";
        caregiver: Caregiver;
        api: API;
        photoUrl: string | null;
    }

    export interface EmptyState {
        type: "EmptyState";
    }

}

interface MainComponentState {
    loginState: LoginState;
    fromLink: boolean;
}

class MainComponent extends React.Component<{}, MainComponentState> {
    constructor(props: {}) {
        super(props);
        if (apiConfig.apiBaseUrl === localApiConfig.apiBaseUrl) {
            Raven.config("https://a85aab075b954331aa6f23d5fe396b08@sentry.io/273362").install();
        }
        this.idleLogout();

        const savedAuthData = localStorage.getItem("agencyMemberAuthInfo");
        const fromLink = localStorage.getItem("fromCode") !== null;

        const a = window.location.href.split("/");
        if (a.findIndex((val) => val === "code") !== -1) {
            this.state = {
                loginState: {
                    type: "EmptyState"
                },
                fromLink
            };
        } else if (savedAuthData !== null && savedAuthData !== undefined) {
            // have saved HR auth data
            window.location.href = "#";
            const savedAgencyMemberAuthInfo = JSON.parse(savedAuthData);

            this.state = {
                loginState: {
                    type: "WithAgencyMemberAuthInfo",
                    agencyMemberAuthInfo: savedAgencyMemberAuthInfo
                },
                fromLink
            };
        } else {
            this.state = {
                loginState: {
                    type: "NoAuth"
                },
                fromLink
            };
        }
    }

    async startIdleLogoutWithAgencyTime(api: API) {
        const logoutTimer = await api.getAgencyLogoutTimerInMilliSeconds();
        idleLogoutTime = logoutTimer.milliseconds;
    }

    idleLogout() {
        let timer: any = null;

        function resetTimer() {
            clearTimeout(timer);
            timer = setTimeout(logout, idleLogoutTime);
        }

        window.onload = resetTimer;
        window.onmousemove = resetTimer;
        window.onmousedown = resetTimer;
        window.onkeydown = resetTimer;
        window.onwheel = resetTimer;
        window.ontouchstart = resetTimer;
        window.ontouchmove = resetTimer;
        window.onclick = resetTimer;
        window.onscroll = resetTimer;

        function logout() {
            location.reload();
        }
    }

    chooseRenderState() {
        switch (this.state.loginState.type) {
            case ("NoAuth"): {
                return this.renderNoAuthState();
            }
            case ("WithAgencyMemberAuthInfo"): {
                return this.renderWithAgencyMemberAuthInfo(this.state.loginState);
            }
            case ("NewCaregiver"): {
                return this.renderNewCaregiver(this.state.loginState);
            }
            case ("WithCaregiverAuthInfo"): {
                return this.renderWithCaregiverAuthInfo(this.state.loginState);
            }
            case "EmptyState":
                return <div />;
            default: {
                return assertNever(this.state.loginState);
            }
        }
    }

    handleCaregiverLoginState(authToken: string, agencyMemberAuthInfo: AgencyMemberAuthInfo, caregiver: Caregiver, photoUrl: string | null) {
        const api: API = new API(apiConfig, {
            caregiver: caregiver,
            authToken: authToken,
            agencyMemberAuthInfo: agencyMemberAuthInfo
        });
        this.startIdleLogoutWithAgencyTime(api);
        this.setState({
            loginState: {
                type: "WithCaregiverAuthInfo",
                caregiver: caregiver,
                api: api,
                photoUrl: photoUrl
            }
        });
    }


    handleCaregiverUpdateDetails(api: API, caregiver: Caregiver, photoUrl: string | null) {
        // TODO use prev state instead of receiving the api, it didnt work for some reason
        this.setState({
            loginState: {
                type: "WithCaregiverAuthInfo",
                caregiver: caregiver,
                api: api,
                photoUrl: photoUrl
            }
        });
    }

    handleAgencyMemberLogin(agencyMemberAuthInfo: AgencyMemberAuthInfo) {
        this.setState({
            loginState: {
                type: "WithAgencyMemberAuthInfo",
                agencyMemberAuthInfo: agencyMemberAuthInfo
            }
        });
    }

    renderNoAuthState() {
        return (
            <Route
                path="/"
                render={props =>
                    <AgencyLoginActivity
                        {...props}
                        onSuccessfulLogin={(agencyMemberAuthInfo) => {
                            this.handleAgencyMemberLogin(agencyMemberAuthInfo);
                            props.history.push("/");
                        }}
                        apiConfig={apiConfig}
                    />}
            />
        );
    }

    renderWithAgencyMemberAuthInfo(state: LoginState.WithAgencyMemberAuthInfo) {
        return (
            <div>
                <Route exact={true} path="/" render={props => <CaregiverLandingScreen {...props} agencyName={state.agencyMemberAuthInfo.agency.name} />} />
                <Route
                    path="/caregiverPhone"
                    render={props =>
                        <CaregiverPhoneEntryActivity
                            {...props}
                            agencyId={state.agencyMemberAuthInfo.agency.id}
                            apiConfig={apiConfig}
                            onSuccessfulLogin={(authToken: string, caregiver: Caregiver | null, phoneNumber: string) => {
                                if (caregiver === null) {
                                    this.setState({
                                        loginState: {
                                            type: "NewCaregiver",
                                            phoneNumber: phoneNumber,
                                            authToken: authToken,
                                            apiConfig: apiConfig,
                                            agencyMemberAuthInfo: state.agencyMemberAuthInfo
                                        }
                                    });
                                } else {
                                    this.handleCaregiverLoginState(authToken, state.agencyMemberAuthInfo, caregiver, null);
                                    props.history.push("/basicDetails");
                                }
                            }}

                        />}
                />
            </div>
        );
    }

    renderNewCaregiver(state: LoginState.NewCaregiver) {
        return (
            <Route
                path={"/"}
                render={props =>
                    <NewCaregiverBasicDetailsActivity
                        {...props}
                        apiConfig={state.apiConfig}
                        authToken={state.authToken}
                        onSuccessfulLogin={(caregiver, photoUrl) => {
                            this.handleCaregiverLoginState(state.authToken, state.agencyMemberAuthInfo, caregiver, photoUrl);
                            props.history.push("/form");
                        }}
                        phoneNumber={state.phoneNumber}
                    />}
            />
        );
    }

    renderWithCaregiverAuthInfo(state: LoginState.WithCaregiverAuthInfo) {
        const caregiverCertifications = this.getCaregiverCertifications();
        return (
            <div>
                <Route
                    exact={true}
                    path={"/basicDetails"}
                    render={props =>
                        <ExistingCaregiverBasicDetailsActivity
                            {...props}
                            caregiver={state.caregiver}
                            api={state.api}
                            onSuccessfulLogin={(caregiver, photoUrl) => {
                                this.handleCaregiverUpdateDetails(state.api, caregiver,  photoUrl);
                                props.history.push("/form");
                            }}
                            photoUrl={state.photoUrl}
                        />}
                />

                <Route
                    path={"/form"}
                    render={props =>
                        <CaregiverFormEntryActivity
                            {...props}
                            matchUrl={props.match.url}
                            api={state.api}
                            photoUrl={state.photoUrl}
                            onCertificationChange={this.handleChangeCertification}
                            caregiverCertifications={caregiverCertifications}
                        />
                    }
                />
            </div>
        );
    }

    updateAuthState = (loginState: LoginState) => {
        this.setState({
            loginState: loginState,
            fromLink: loginState.type === "WithAgencyMemberAuthInfo"
        });
    }

    globalRoutes() {
        return (
            <Route
                path="/code"
                render={props => {
                    return <ShortCodeAuth apiConfig={apiConfig} updateUpperState={this.updateAuthState} path={props.location.pathname} />;
                }}
            />
        );
    }

    handleChangeCertification = (certification: CaregiverCertification) => {
        const loginState = this.state.loginState;
        if (loginState.type !== "WithCaregiverAuthInfo") {
            return;
        }

        if (!loginState.caregiver.certifications.includes(certification)) {
            loginState.caregiver.certifications.push(certification);
        }
        this.setState({ loginState: loginState, fromLink: this.state.fromLink });
    }

    getCaregiverCertifications = () => {
        if (this.state.loginState.type !== "WithCaregiverAuthInfo") {
            return [];
        }

        return this.state.loginState.caregiver.certifications;
    }

    render() {
        return (
            <div className={"Index_root"}>
                <NavBar
                    hideLogout={this.state.fromLink}
                />
                {this.chooseRenderState()}
                {this.globalRoutes()}
            </div>
        );

    }
}

ReactDOM.render(
    <HashRouter>
        <Provider store={store}>
            <MainComponent/>
        </Provider>
    </HashRouter>,
    document.getElementById("root") as HTMLElement
);
// registerServiceWorker();



// WEBPACK FOOTER //
// ./src/index.tsx